import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  HostListener,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from "@angular/forms";
import { LocalStorageService } from "../../services/local-storage.service";
import { User, Gender } from "../../model/user";
import { RouterService } from "../../services/router.service";
import { CustomNotificationService } from "../../services/custom-notification.service";
import { CustomModalService } from "../../services/custom-modal.service";
import { UserService } from "../../services/user.service";
import { AuthenticationService } from "../../services/authentication.service";
import { CameraService } from "../../services/camera.service";
import { HapticService } from "../../services/haptic.service";
import { LucideAngularModule } from "lucide-angular";

@Component({
  selector: "app-edit-profile",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule],
  templateUrl: "./edit-profile.component.html",
  styleUrls: ["./edit-profile.component.css"],
})
export class EditProfileComponent implements OnInit {
  profileForm!: FormGroup;
  currentUser: User = {} as User;
  users: User[] = [];
  loading = true;
  genderDropdownOpen = false;
  selectedGender: string = "null";
  dangerZoneExpanded = false;
  @ViewChild("fileInput") fileInput?: ElementRef<HTMLInputElement>;

  readonly defaultImage = "../../assets/images/profile-pic.png";

  genderOptions = [
    { label: "Male", value: "false" },
    { label: "Female", value: "true" },
    { label: "Prefer not to say", value: "null" },
  ];

  constructor(
    private fb: FormBuilder,
    private localStorageService: LocalStorageService,
    private routerService: RouterService,
    private userService: UserService,
    private authService: AuthenticationService,
    private notificationService: CustomNotificationService,
    private modalService: CustomModalService,
    private cameraService: CameraService,
    private hapticService: HapticService,
  ) {}

  async ngOnInit(): Promise<void> {
    const id = this.authService.currentUserValue?.id;
    this.users = (await this.localStorageService.getItem("users")) || [];

    this.profileForm = this.fb.group({
      username: [{ value: "", disabled: true }, Validators.required],
      name: ["", Validators.required],
      last_name: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      phone: ["", Validators.required],
      availableToPlay: [false],
    });

    if (id) {
      this.userService.getUser(id).subscribe({
        next: (u: User) => {
          this.currentUser = u;

          let genderValue = "null";
          if ((u.gender as any) === true) {
            genderValue = "true";
          } else if ((u.gender as any) === false) {
            genderValue = "false";
          }
          this.selectedGender = genderValue;

          this.profileForm.patchValue({
            username: u.username,
            name: u.name,
            last_name: u.last_name,
            email: u.email || u.mail,
            phone: u.phone_number,
            availableToPlay:
              (u as any).available_to_play ?? (u as any).availableToPlay,
          });
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
    } else {
      this.loading = false;
    }
  }

  isInvalid(control: string): boolean {
    if (control === "gender") {
      return !this.selectedGender;
    }
    const c = this.profileForm.get(control);
    return !!c && c.invalid && (c.dirty || c.touched);
  }

  toggleGenderDropdown(): void {
    this.genderDropdownOpen = !this.genderDropdownOpen;
  }

  selectGender(option: { label: string; value: string }): void {
    this.selectedGender = option.value;
    this.genderDropdownOpen = false;
  }

  getSelectedGenderLabel(): string {
    const selected = this.genderOptions.find(
      (option) => option.value === this.selectedGender,
    );
    return selected ? selected.label : "Select gender";
  }

  trackByValue(index: number, item: any): any {
    return item.value;
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest(".custom-dropdown")) {
      this.genderDropdownOpen = false;
    }
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file: File | undefined = input.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.currentUser.profile_image = reader.result as string;
      if (this.fileInput) {
        this.fileInput.nativeElement.value = "";
      }
    };
    reader.readAsDataURL(file);
  }

  triggerFileInput(): void {
    this.fileInput?.nativeElement.click();
  }

  async takePhoto(): Promise<void> {
    this.hapticService.light();
    try {
      const photo = await this.cameraService.takePhoto();
      if (photo && photo.dataUrl) {
        this.currentUser.profile_image = photo.dataUrl;
        this.hapticService.success();
        this.notificationService.success(
          "Success",
          "Photo captured successfully",
        );
      } else {
        this.notificationService.warning(
          "Cancelled",
          "Photo capture was cancelled",
        );
      }
    } catch (error) {
      this.hapticService.error();
      this.notificationService.error("Error", "Failed to capture photo");
    }
  }

  async selectFromGallery(): Promise<void> {
    this.hapticService.light();
    try {
      const photo = await this.cameraService.pickFromGallery();
      if (photo && photo.dataUrl) {
        this.currentUser.profile_image = photo.dataUrl;
        this.hapticService.success();
        this.notificationService.success(
          "Success",
          "Photo selected successfully",
        );
      } else {
        this.notificationService.warning(
          "Cancelled",
          "Photo selection was cancelled",
        );
      }
    } catch (error) {
      this.hapticService.error();
      this.notificationService.error("Error", "Failed to select photo");
    }
  }

  async pickPhoto(): Promise<void> {
    this.hapticService.light();
    try {
      const photo = await this.cameraService.pickPhoto();
      if (photo && photo.dataUrl) {
        this.currentUser.profile_image = photo.dataUrl;
        this.hapticService.success();
        this.notificationService.success(
          "Success",
          "Photo selected successfully",
        );
      } else {
        this.notificationService.warning(
          "Cancelled",
          "Photo selection was cancelled",
        );
      }
    } catch (error) {
      this.hapticService.error();
      this.notificationService.error("Error", "Failed to select photo");
    }
  }

  onSave(): void {
    this.hapticService.medium();
    if (this.profileForm.invalid || !this.selectedGender) {
      this.profileForm.markAllAsTouched();
      this.hapticService.warning();
      return;
    }
    const formValue = this.profileForm.getRawValue();
    const updatedUser: User = {
      ...this.currentUser,
      name: formValue.name,
      last_name: formValue.last_name,
      gender:
        this.selectedGender === "null" ? null : this.selectedGender === "true",
      mail: formValue.email,
      email: formValue.email,
      phone_number: formValue.phone,
      availableToPlay: formValue.availableToPlay,
      available_to_play: formValue.availableToPlay,
      profile_image: this.currentUser.profile_image,
      roles: this.currentUser.roles || [],
    } as any;

    this.userService.updateUserWithBody(updatedUser).subscribe({
      next: (u) => {
        this.currentUser = u;
        this.localStorageService.setItem("currentUser", u);
        sessionStorage.setItem("currentUser", JSON.stringify(u));
        (this.authService as any).currentUserSubject.next(u);
        this.saveCurrentUser(u);
        this.hapticService.success();
        this.notificationService.success("Saved", "Profile updated");
      },
      error: () => {
        this.hapticService.error();
        this.notificationService.error("Error", "Failed to update profile");
      },
    });
  }

  saveCurrentUser(user: User): void {
    for (let i = 0; i < this.users.length; i++) {
      if (this.users[i].id === user.id) {
        this.users[i] = user;
        this.localStorageService.setItem("users", this.users);
        return;
      }
    }
  }

  toggleAvailability(): void {
    const currentValue = this.profileForm.get("availableToPlay")?.value;
    this.profileForm.patchValue({ availableToPlay: !currentValue });
  }

  onLogout(): void {
    this.routerService.navigateTo("logout");
  }

  toggleDangerZone(): void {
    this.dangerZoneExpanded = !this.dangerZoneExpanded;
    this.hapticService.light();
  }

  async onDeleteAccount(): Promise<void> {
    this.hapticService.warning();

    const confirmed = await this.modalService.confirm({
      title: "Delete Account",
      message:
        "Are you absolutely sure you want to delete your account? This action cannot be undone. All your data, matches, and statistics will be permanently deleted.",
      confirmText: "Delete Account",
      cancelText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    const doubleConfirmed = await this.modalService.confirm({
      title: "Final Confirmation",
      message:
        "This is your last chance. Your account will be permanently deleted. Type DELETE in the text below to confirm.",
      confirmText: "Confirm Deletion",
      cancelText: "Cancel",
    });

    if (!doubleConfirmed) {
      return;
    }

    this.loading = true;
    const userId = this.currentUser.id;
    const token = this.authService.currentUserValue?.jwtToken;

    this.userService.deleteAccount(userId, token).subscribe({
      next: async () => {
        this.loading = false;
        this.hapticService.success();
        this.notificationService.success(
          "Account Deleted",
          "Your account has been successfully deleted. Redirecting to login...",
        );

        await this.authService.logout();
        await this.localStorageService.clear();

        setTimeout(() => {
          this.routerService.navigateTo("login");
        }, 2500);
      },
      error: (error) => {
        this.loading = false;
        this.hapticService.error();
        console.error("Account deletion failed:", error);
        this.notificationService.error(
          "Error",
          "Failed to delete account. Please contact support.",
        );
      },
    });
  }
}
